import Avatar, { AvatarProps } from '@/components/avatar/Avatar';
import { Icon, IconProps, variants } from '@/components/icon/Icon';
import LegendPopover, { LegendPopoverPropsType } from '@/lib/_components/LegendPopover';
import { cn } from '@/lib/_utils/cn';
import { cva, cx, VariantProps } from 'class-variance-authority';
import Image, { ImageProps } from 'next/image';
import { ReactNode } from 'react';

export const variantsHeader = cva(['flex', 'items-start', 'justify-between', 'gap-4', 'relative'], {
	variants: {
		size: {
			sm: ['p-lg'],
			md: ['p-3xl'],
			lg: ['p-5xl'],
		},
		isChildrenWithPadding: {
			true: [],
			false: [],
		},
		titleVariant: {
			h4: ['text-white'],
			h5: [],
		},
	},
	compoundVariants: [],
	defaultVariants: {
		size: 'md',
	},
});

export const variantsHeaderTitle = cva([], {
	variants: {
		size: {},
		titleVariant: {
			h3: ['text-white'],
			h4: ['text-white'],
			h5: [],
		},
	},
	defaultVariants: {
		titleVariant: 'h4',
	},
});

export type BlockHeaderDataProps = {
	icon?: Partial<IconProps>;
	image?: Partial<ImageProps>;
	avatar?: Partial<AvatarProps>;
	title?: string;
	titleProps?: React.HTMLAttributes<HTMLDivElement>;
	titleVariant?: 'h4' | 'h5' | 'h3';
	subtitle?: string;
	legendPopoverProps?: Partial<LegendPopoverPropsType>;
	action?: ReactNode;
	actionProps?: React.HTMLAttributes<HTMLDivElement>;
} & React.HTMLAttributes<HTMLDivElement>;

type BlockHeaderGlobalProps = BlockHeaderDataProps &
	VariantProps<typeof variants> & {
		size?: 'sm' | 'md' | 'lg';
	};

const BlockHeader = ({ size, ...props }: BlockHeaderGlobalProps) => {
	const {
		icon,
		avatar,
		image,
		title,
		titleProps,
		subtitle,
		legendPopoverProps,
		action,
		actionProps,
		titleVariant = 'h4',
		className,
	} = props;
	return (
		<div id='BlockHeader' className={`${cx(variantsHeader({ size, className }))}`}>
			<div>
				<div className='flex items-center gap-lg'>
					{icon && (
						<div>
							<Icon name={'arrowCandlestickIcon'} {...icon} />
						</div>
					)}
					{avatar && (
						<div>
							<Avatar {...avatar} />
						</div>
					)}
					{image && (
						<div>
							<Image src={''} width={20} height={20} alt='' {...image} />
						</div>
					)}
					{title && (
						<div className='grid gap-md break-all'>
							{titleVariant === 'h3' && (
								<h3 {...titleProps} className={`${cx(variantsHeaderTitle({ titleVariant }), titleProps?.className)}`}>
									{title}
								</h3>
							)}
							{titleVariant === 'h4' && (
								<h4 {...titleProps} className={`${cx(variantsHeaderTitle({ titleVariant }), titleProps?.className)}`}>
									{title}
								</h4>
							)}
							{titleVariant === 'h5' && (
								<h5 {...titleProps} className={`${cx(variantsHeaderTitle({ titleVariant }), titleProps?.className)}`}>
									{title}
								</h5>
							)}
						</div>
					)}
					{legendPopoverProps && <LegendPopover legendData={[]} {...legendPopoverProps} />}
				</div>
				{subtitle && <p className='mt-sm text-md text-dark-300'>{subtitle}</p>}
			</div>
			{action && (
				<div {...actionProps} className={`${cn('flex flex-none items-center gap-4', actionProps?.className)}`}>
					{action}
				</div>
			)}
		</div>
	);
};

BlockHeader.displayName = 'BlockHeader';
export default BlockHeader;
